import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cva, type VariantProps } from 'class-variance-authority';
import { motion } from 'motion/react';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { useMotionProps } from '@/lib/cva-motion';

const switchVariants = cva(
  'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=unchecked]:bg-muted data-[state=unchecked]:border-border',
  {
    variants: {
      variant: {
        default: 'data-[state=checked]:bg-primary data-[state=checked]:border-primary',
        success: 'data-[state=checked]:bg-success data-[state=checked]:border-success',
        emerald: 'data-[state=checked]:bg-emerald data-[state=checked]:border-emerald',
        purple: 'data-[state=checked]:bg-purple data-[state=checked]:border-purple',
        gold: 'data-[state=checked]:bg-gold data-[state=checked]:border-gold',
        pink: 'data-[state=checked]:bg-pink data-[state=checked]:border-pink',
      },
      size: {
        sm: 'h-4 w-7',
        md: 'h-6 w-11',
        lg: 'h-8 w-14',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

const switchThumbVariants = cva(
  'pointer-events-none block rounded-full bg-background shadow-lg ring-0 transition-transform',
  {
    variants: {
      size: {
        sm: 'h-3 w-3 data-[state=checked]:translate-x-3 data-[state=unchecked]:translate-x-0',
        md: 'h-5 w-5 data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0',
        lg: 'h-7 w-7 data-[state=checked]:translate-x-7 data-[state=unchecked]:translate-x-0',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export interface SwitchProps
  extends
    React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
    VariantProps<typeof switchVariants> {}

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, variant, size, ...props }, ref) => (
    <SwitchPrimitives.Root
      className={cn(switchVariants({ variant, size }), className)}
      {...props}
      ref={ref}
    >
      <SwitchPrimitives.Thumb className={cn(switchThumbVariants({ size }))} />
    </SwitchPrimitives.Root>
  ),
);
Switch.displayName = SwitchPrimitives.Root.displayName;

/**
 * Motion-enhanced Switch component with automatic animations.
 *
 * Provides the same API as Switch but with built-in motion animations:
 * - Scale animation on thumb when toggled
 * - Subtle scale on hover
 * - Smooth focus transitions
 *
 * @example
 * ```tsx
 * <MotionSwitch variant="emerald" size="md" />
 * ```
 */
const MotionSwitch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, variant, size, ...props }, ref) => {
    const motionProps = useMotionProps({
      hover: 'scale',
      tap: 'scale',
      focus: 'glow',
      initial: 'none',
    });

    return (
      <SwitchPrimitives.Root
        className={cn(switchVariants({ variant, size }), className)}
        {...props}
        ref={ref}
        asChild
      >
        <motion.button
          {...(motionProps as any)}
          className={cn(switchVariants({ variant, size }), className)}
        >
          <SwitchPrimitives.Thumb className={cn(switchThumbVariants({ size }))} />
        </motion.button>
      </SwitchPrimitives.Root>
    );
  },
);
MotionSwitch.displayName = 'MotionSwitch';

export { MotionSwitch, Switch, switchVariants };
